import React, { useState, useEffect, useRef } from "react";
import domtoimage from "dom-to-image-more";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const ExportDropdown = ({ showController, setShowController, exportRef, mapRef, apiResponse, visualizationConfig }) => {
  const [open, setOpen] = useState(false);
  const toggleDropdown = () => {
    setOpen(!open);
  };
  const dropdownRef = useRef(null);
  // 👇 Close dropdown on click outside or ESC key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [open, dropdownRef]);


  // Convert image URL to base64
  const imageToBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  // Embed pattern images as base64 before export
  const embedPatterns = async () => {
    const patternPaths = [
      '/newpatterns/p1-2.png',
      '/patterns/p2.png',
      '/newpatterns/p3-2.png',
      '/patterns/p4.png',
      '/patterns/p5.png',
      '/patterns/p6.png'
    ];

    const patternIds = [
      'sandyPattern',
      'clayPattern',
      'loamPattern',
      'rockyPattern',
      'betaPattern',
      'noPattern'
    ];

    try {
      const svgDefs = document.querySelector('svg defs');
      if (!svgDefs) return;

      for (let i = 0; i < patternPaths.length; i++) {
        const pattern = document.getElementById(patternIds[i]);
        if (pattern) {
          const image = pattern.querySelector('image');
          if (image) {
            const base64 = await imageToBase64(patternPaths[i]);
            image.setAttribute('href', base64);
          }
        }
      }
    } catch (error) {
      console.error('Error embedding patterns:', error);
    }
  };

  const handleExport = async (type) => {
    const element = exportRef?.current;
    if (!element) return;
    setOpen(false);

    try {
      // Force map resize if needed
      if (mapRef?.current?.forceResize) {
        mapRef.current.forceResize();
      }

      // Embed patterns before export
      await embedPatterns();

      // Force white background before exporting
      const originalBg = element.style.backgroundColor;
      element.style.backgroundColor = "#ffffff";
      setShowController(false);
      // Wait a moment for patterns to be applied
      await new Promise(resolve => setTimeout(resolve, 500));

      // Convert the map container to an image with enhanced options
      const dataUrl = await domtoimage.toPng(element, {
        quality: 1,
        bgcolor: "#ffffff",
        cacheBust: true,
        width: element.offsetWidth * 2,
        height: element.offsetHeight * 2,
        style: {
          transform: "scale(2)",
          transformOrigin: "top left",
          backgroundColor: "#ffffff",
        },
        // Add filter to exclude non-visible elements
        filter: (node) => {
          return !node.classList?.contains('leaflet-control-container');
        }
      });

      const timestamp = new Date().toISOString()
        .replace(/T/, '_')
        .replace(/:/g, '-')
        .replace(/\..+/, '');

      // if (type === "png") {
      //   download(dataUrl, `map_${timestamp}.png`);
      // } else
      if (type === "jpg") {
        const jpgData = await domtoimage.toJpeg(element, {
          quality: 2,
          bgcolor: "#ffffff",
          style: { backgroundColor: "#ffffff" },
          width: element.offsetWidth * 1,
          height: element.offsetHeight * 1,
          filter: (node) => {
            return !node.classList?.contains('leaflet-control-container');
          }
        });
        download(jpgData, `map_${timestamp}.jpg`);
      }
      // else if (type === "pdf") {
      //   const img = new Image();
      //   img.src = dataUrl;
      //   img.onload = () => {
      //     const pdf = new jsPDF({
      //       orientation: img.width > img.height ? "l" : "p",
      //       unit: "px",
      //       format: [img.width, img.height],
      //     });

      //     // Fill PDF background white
      //     pdf.setFillColor(255, 255, 255);
      //     pdf.rect(0, 0, img.width, img.height, "F");

      //     pdf.addImage(dataUrl, "PNG", 0, 0, img.width, img.height);
      //     pdf.save(`map_${timestamp}.pdf`);
      //   };
      // }

      // Restore original background
      element.style.backgroundColor = originalBg;
    } catch (err) {
      alert("Export failed. Please try again.", err);
    }
    setShowController(true);
  };
  // 🟨 Export as CSV (new function)
  const handleExportCSV = () => {
    if (!apiResponse || !visualizationConfig) {
      alert("No data available to export.");
      return;
    }

    setOpen(false);

    try {
      const colorIndicator = visualizationConfig.colorIndicator;
      const patternIndicator = visualizationConfig.patternIndicator;
      const indicators = visualizationConfig.indicators || [];

      const headers = [
        "State Name",
        patternIndicator?.label || "Pattern Indicator",
        colorIndicator?.label || "Color Indicator",
        ...indicators.map((ind) => ind.label || ind.name || "Indicator"),
      ];

      const rows = apiResponse.indicator_values.map((item) => {
        const getIndicatorValue = (indicator) => {
          if (!indicator) return '';
          const code = indicator.code;
          const subCode = indicator.subIndicator?.code;
          const key = subCode ? `${code}#${subCode}` : `${code}#`;
          return item[key] ?? '';
        };

        return [
          item.name || '',
          getIndicatorValue(patternIndicator),
          getIndicatorValue(colorIndicator),
          ...indicators.map(ind => getIndicatorValue(ind))
        ];
      });

      const csvContent =
        headers.join(",") +
        "\n" +
        rows.map((r) => r.map(cell => {
          // Escape cells containing commas or quotes
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(",")).join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = URL.createObjectURL(blob);
      link.download = `data_export_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("CSV export error:", error);
      alert("CSV export failed. Please try again.");
    }
  };

  const download = (dataUrl, filename) => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="dropdown mt-3 position-relative" ref={dropdownRef}>
      <button
        className="btn btn-success  d-flex align-items-center gap-2"
        type="button"
        onClick={toggleDropdown}
      >
        Export {open ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      <ul className={`dropdown-menu ${open ? "show" : ""}`} style={{ position: "absolute" }}>
        <li>
          <button className="dropdown-item" onClick={() => handleExport("jpg")}>
            Export as Image
          </button>
        </li>
        <li>
          <button className="dropdown-item" onClick={handleExportCSV}>
            Export as CSV
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ExportDropdown;